import React from 'react';
import { Box, makeStyles, Typography, useTheme } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { formatMoney } from '../../utils/formatMoney';

type PricingFormProps = {
  title: string;
  price?: number;
  description: string;
};

type ServiceProps = {
  title: string;
  price?: number;
  quantity: number;
};

type SummaryProps = {
  title: string;
  price?: number;
  bold?: boolean;
  boldPrice?: boolean;
};

type Props = (PricingFormProps | ServiceProps | SummaryProps) & {
  testId?: string;
};

type StyleProps = {
  bold: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  lineItem: {
    flex: '1 1 auto',
    minWidth: 0,
    margin: `${theme.spacing(0.5)}px 0`,
    height: ({ bold }: StyleProps) => (bold ? theme.spacing(3) : 'auto'),
  },
  name: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 'normal',
    [theme.breakpoints.down('md')]: {
      whiteSpace: ({ bold }: { bold: boolean }) => (bold ? 'nowrap' : 'normal'),
    },
  },
  titleItem: {
    minWidth: 0,
    flex: '0 1 auto',
  },
  priceItem: {
    minWidth: 0,
    flex: '0 0 auto',
  },
  priceText: {
    minWidth: theme.spacing(10),
    textAlign: 'right',
    [theme.breakpoints.down('md')]: {
      minWidth: theme.spacing(7),
    },
  },
}));

export const CartItemDisplay = ({ title, price, testId, ...rest }: Props) => {
  const bold = 'bold' in rest ? rest.bold : false;
  const boldPrice = 'boldPrice' in rest ? rest.boldPrice : false;
  const quantity = 'quantity' in rest ? rest.quantity : 1;
  const description = 'description' in rest ? rest.description : undefined;

  const { lineItem, name, titleItem, priceItem, priceText } = useStyles({
    bold: Boolean(bold),
  });

  const theme = useTheme();
  const isMobile = theme.breakpoints.down('md');

  return (
    <div className={lineItem}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" mr={isMobile ? 0.5 : 1} className={titleItem}>
          <Typography className={name} variant={bold ? 'subtitle2' : 'body2'}>
            {quantity > 1 ? `${quantity}*` : ''}
            {title}
          </Typography>
        </Box>
        <Box ml={isMobile ? 0 : 1} display="flex" alignItems="center" className={priceItem}>
          {typeof price !== 'undefined' && (
            <Typography
              data-testid={testId || 'service-price'}
              className={priceText}
              variant={bold || boldPrice ? 'subtitle2' : 'body2'}
            >
              {formatMoney(quantity * price)}
            </Typography>
          )}
        </Box>
      </Box>
      {description && (
        <Box mt={1}>
          <Typography className={name} variant="body2">
            {description}
          </Typography>
        </Box>
      )}
    </div>
  );
};
